import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, firstValueFrom, map, of } from 'rxjs';
import { DomicilioDTO } from './domicilios.models';
import {
  ListaDTO,
  Parametros,
} from '../../compartido/utilidades/compartido.models';
import { ErrorFrontend } from 'src/app/compartido/utilidades/error.utils';
import { Domicilio } from './domicilio.class';
import { stringAleatorio } from 'src/app/compartido/utilidades/valores.utils';

@Injectable({ providedIn: 'root', deps: [HttpClient] })
export class DomiciliosService {
  constructor(private readonly _http: HttpClient) {}

  /**
   * Obtiene los domicilios del servidor.
   * @param {Parametros} params - Los parámetros de búsqueda.
   * @returns {Promise<ListaDTO<DomicilioDTO>>} - Los domicilios obtenidos del servidor (promise).
   * @throws {ErrorFrontend} - Si hubo un error al obtener los domicilios.
   * @author Juan Corral
   */
  public obtenerDomicilios$(
    params: Parametros,
  ): Promise<ListaDTO<DomicilioDTO>> {
    return firstValueFrom(
      this._http.get<ListaDTO<DomicilioDTO>>('domicilios/', { params }).pipe(
        catchError((error: HttpErrorResponse) => {
          throw new ErrorFrontend(error);
        }),
      ),
    );
  }

  /**
   * Obtiene información del domicilio del servidor.
   * @param {number} id - El id del domicilio a obtener.
   * @returns {Promise<Domicilio> } - La respuesta del servidor (promise).
   * @throws {ErrorFrontend} - Si hubo un error al obtener el domicilio.
   * @author Juan Corral
   */
  public obtenerDomicilio$(id: number): Promise<Domicilio> {
    return firstValueFrom(
      this._http.get<DomicilioDTO>('domicilios/' + id).pipe(
        map((domicilio: DomicilioDTO) => Domicilio.fromDTO(domicilio)),
        catchError((error: HttpErrorResponse) => {
          throw new ErrorFrontend(error);
        }),
      ),
    );
  }

  /**
   * Sincroniza un domicilio con SimpliRoute.
   * @param {number} id - El id del domicilio a sincronizar.
   * @returns {Promise<Domicilio> } - El domicilio tras la sincronización (promise).
   * @throws {ErrorFrontend} - Si hubo un error al sincronizar el domicilio.
   * @author Juan Corral
   */
  public sincronizarDomicilio$(id: number): Promise<Domicilio> {
    return firstValueFrom(
      this._http.get<DomicilioDTO>(`domicilios/${id}/sincronizar/`).pipe(
        map((domicilio: DomicilioDTO) => Domicilio.fromDTO(domicilio)),
        catchError((error: HttpErrorResponse) => {
          throw new ErrorFrontend(error);
        }),
      ),
    );
  }

  /**
   * Cancela un domicilio.
   * @param {number} id - El id del domicilio a cancelar.
   * @param {string} motivo - El motivo de la cancelación.
   * @param {string} observacion [Opcional] - La observación de la cancelación.
   * @throws {ErrorFrontend} - Si hubo un error al cancelar el domicilio.
   * @autor Juan Corral
   */
  public async cancelarDomicilio$(
    id: number,
    motivo: string,
    observacion?: string,
  ): Promise<void> {
    await firstValueFrom(
      this._http
        .post<void>('domicilios/' + id + '/cancelar/', { motivo, observacion })
        .pipe(
          catchError((error: HttpErrorResponse) => {
            throw new ErrorFrontend(error);
          }),
        ),
    );
  }

  /**
   * Despacha un domicilio.
   * @param {number} id - El id del domicilio a despachar.
   * @throws {ErrorFrontend} - Si hubo un error al despachar el domicilio.
   * @autor Juan Corral
   */
  public async despacharDomicilio$(id: number): Promise<void> {
    await firstValueFrom(
      this._http.post<void>('domicilios/' + id + '/despachar/', {}).pipe(
        catchError((error: HttpErrorResponse) => {
          throw new ErrorFrontend(error);
        }),
      ),
    );
  }

  /**
   * Entrega un domicilio.
   * @param {number} id - El id del domicilio a entregar.
   * @param {boolean} entregado - Si el domicilio fue entregado.
   * @param {File} foto - La foto de evidencia de la entrega.
   * @param {string} observacion [Opcional] - La observación de la entrega.
   * @param {string} comentarios [Opcional] - Los comentarios de la entrega.
   * @param {number} latitud [Opcional] - La latitud de la entrega.
   * @param {number} longitud [Opcional] - La longitud de la entrega.
   * @throws {ErrorFrontend} - Si hubo un error al entregar el domicilio.
   * @autor Juan Corral
   */
  public async entregarDomicilio$(
    id: number,
    entregado: boolean,
    foto?: File,
    observacion?: string,
    comentarios?: string,
    latitud?: number,
    longitud?: number,
  ): Promise<void> {
    const datos = new FormData();
    datos.append('entregado', entregado ? 'true' : 'false');
    if (foto !== undefined) datos.append('foto', foto, foto.name);
    if (observacion !== undefined) datos.append('observacion', observacion);
    if (comentarios !== undefined) datos.append('comentarios', comentarios);
    if (latitud !== undefined) datos.append('latitud', latitud.toString());
    if (longitud !== undefined) datos.append('longitud', longitud.toString());
    await firstValueFrom(
      this._http.post<void>('domicilios/' + id + '/entregar/', datos).pipe(
        catchError((error: HttpErrorResponse) => {
          throw new ErrorFrontend(error);
        }),
      ),
    );
  }

  /**
   * Legaliza un domicilio.
   * @param {number} id - El id del domicilio a legalizar.
   * @param {File} archivo [Opcional] - El archivo de legalización.
   * @param {string} observacion [Opcional] - La observación de la legalización.
   * @param {boolean} reprogramar [Opcional] - Si se debe re-programar el domicilio.
   * @throws {ErrorFrontend} - Si hubo un error al legalizar el domicilio.
   * @autor Juan Corral
   */
  public async legalizarDomicilio$(
    id: number,
    archivo?: File,
    observacion?: string,
    reprogramar?: boolean,
  ): Promise<Domicilio> {
    const datos = new FormData();
    if (archivo !== undefined) {
      const extension = archivo.name.split('.').pop();
      datos.append(
        'archivo',
        archivo,
        `${id}_${stringAleatorio(7)}.${extension}`,
      );
    }
    if (observacion !== undefined) datos.append('observacion', observacion);
    if (reprogramar) datos.append('reprogramar', 'true');
    return await firstValueFrom(
      this._http
        .post<DomicilioDTO>('domicilios/' + id + '/legalizar/', datos)
        .pipe(
          map((domicilio: DomicilioDTO) => Domicilio.fromDTO(domicilio)),
          catchError((error: HttpErrorResponse) => {
            throw new ErrorFrontend(error);
          }),
        ),
    );
  }

  /**
   * Actualiza un domicilio.
   * @param {number} id - El id del domicilio a actualizar.
   * @param {string} estado [Opcional] - El nuevo estado del domicilio.
   * @param {File} imagen [Opcional] - La nueva imagen del domicilio.
   * @throws {ErrorFrontend} - Si hubo un error al actualizar el domicilio.
   * @autor Juan Corral
   */
  public async actualizarDomicilio$(
    id: number,
    estado?: string,
    imagen?: File,
  ): Promise<void> {
    const formData = new FormData();
    if (estado !== undefined) formData.append('estado', estado);
    if (imagen !== undefined) formData.append('imagen', imagen, imagen.name);
    await firstValueFrom(
      this._http.patch<void>('domicilios/' + id, formData).pipe(
        catchError((error: HttpErrorResponse) => {
          throw new ErrorFrontend(error);
        }),
      ),
    );
  }
}
